

import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { useLoader } from '../../../../context/LoaderProvider.jsx';
import {
    getPartnerDetails, getAllPartners, updatePartnerDetails, getAllDeliveryPartners,
    verifyPartnerField, rejectPartnerField,
    approvePartner, rejectPartnerApproval
} from "../../../Servicefile/clientManagementService.jsx";
// Delivery Partner specific field configuration
const getDefaultDeliveryPartnerRows = () => [
    { key: "Full Name", value: "", isUpload: false },
    { key: "Phone Number", value: "", isUpload: false },
    { key: "Email", value: "", isUpload: false },
    { key: "Profile Picture", value: null, isUpload: false },
    { key: "AADHAR Number", value: "", isUpload: false },
    { key: "PAN Number", value: "", isUpload: false },
    { key: "License Number", value: "", isUpload: false },
    { key: "Vehicle Types", value: [], isUpload: false },
    { key: "Vehicles", value: [], isUpload: false },
    { key: "Petrolium Types", value: [], isUpload: false },
    { key: "RC Number", value: "", isUpload: false },
    { key: "Insurance Number", value: "", isUpload: false },
    {
        key: "Bank Details", value: {
            bankName: "",
            accountNumber: "",
            accountHolderName: "",
            ifscCode: "",
            branchName: "",
            upiId: ""
        }, isUpload: false
    },
    {
        key: "Current Address", value: {
            address: "",
            city: "",
            state: "",
            pinCode: "",
            country: "",
            houseNo: "",
            street: ""
        }, isUpload: false
    },
    {
        key: "Permanent Address", value: {
            permanentAddress: "",
            permanentCity: "",
            permanentState: "",
            permanentPinCode: "",
            permanentCountry: "",
            permanentHouseNo: "",
            permanentStreet: ""
        }, isUpload: false
    }
];

// Vehicle Types options
const vehicleTypeOptions = [
    "BIKE",
    "PASSENGER_AUTO",
    "CAR",
    "MINI_TRUCK",
    "TRUCK"
];

// Vehicles options
const vehiclesOptions = [
    "TwoWheeler",
    "ThreeWheeler",
    "FourWheeler",
    "HeavyVehicle"
];

// Petroleum Types options
const petroliumTypeOptions = [
    "PETROL",
    "DIESEL",
    "CNG",
    "ELECTRIC"
];

// Status field mapping for delivery partners
const STATUS_FIELD_MAP = {
    "Full Name": "fullNameStatus",
    "Phone Number": "phoneNumberStatus",
    "Email": "emailStatus",
    "Profile Picture": "profilePictureStatus",
    "AADHAR Number": "aadharNumberStatus",
    "PAN Number": "panNumberStatus",
    "License Number": "licenseNumberStatus",
    "Vehicle Types": "vehicleTypesStatus",
    "Vehicles": "vehiclesStatus",
    "Petrolium Types": "petroliumTypesStatus",
    "RC Number": "rcNumberStatus",
    "Insurance Number": "insuranceNumberStatus",
    "Bank Details": "bankDetailsStatus",
    "Current Address": "addressStatus",
    "Permanent Address": "permanentAddressStatus"
};

// Safe value display utility
const safeDisplay = (value) => {
    if (value === null || value === undefined) return "N/A";
    if (typeof value === 'string' && value.trim() === "") return "N/A";
    if (value === "N/A" || value === "Invalid Date") return "N/A";
    return value;
};

// Add this function to check if a field has valid data
const hasValidData = (row, partnerDetails) => {
    if (!row || !partnerDetails) return false;

    const field = row.key;
    const value = row.value;

    // Check if the value exists and is not empty/"N/A"
    if (typeof value === 'string') {
        return value && value !== "N/A" && value.trim() !== "";
    }

    if (Array.isArray(value)) {
        return value.length > 0 && value.some(item => item && item !== "N/A");
    }

    if (typeof value === 'object') {
        // For objects, check if any property has valid data
        return Object.values(value).some(val => {
            if (val === null || val === undefined) return false;
            if (typeof val === 'string') return val.trim() !== "" && val !== "N/A";
            if (Array.isArray(val)) return val.length > 0;
            return true;
        });
    }

    return false;
};

const isFieldVerified = (rowKey, rowValue, partnerDetails) => {
    const normalisedKey = rowKey.trim().replace(/\.$/, "");
    const statusField = STATUS_FIELD_MAP[normalisedKey];

    if (!statusField || !partnerDetails) return false;

    if (partnerDetails[statusField] === true) return true;

    if ((partnerDetails[statusField] === null || partnerDetails[statusField] === undefined) &&
        hasValidData({ key: rowKey, value: rowValue }, partnerDetails)) {
        return true;
    }

    return false;
};

const isFieldRejected = (rowKey, partnerDetails) => {
    const normalisedKey = rowKey.trim().replace(/\.$/, "");
    const statusField = STATUS_FIELD_MAP[normalisedKey];

    if (!statusField || !partnerDetails) return false;

    return partnerDetails[statusField] === false;
};

/* ---------------- component ---------------- */
const Logisticssubscribedclients = () => {
    /* -------- list‑screen state -------- */
    const [partnersData, setPartnersData] = useState([]);
    const [statusFilter, setStatusFilter] = useState("");
    const [vehicleFilter, setVehicleFilter] = useState("");
    const [cityFilter, setCityFilter] = useState("");
    const [filteredData, setFilteredData] = useState([]);
    const { showLoader, hideLoader } = useLoader();

    /* -------- detail‑screen state -------- */
    const [selectedPartner, setSelectedPartner] = useState(null);
    const [partnerDetails, setPartnerDetails] = useState(null);
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState(getDefaultDeliveryPartnerRows());
    const [verifiedRows, setVerifiedRows] = useState([]);
    const [rejectedRows, setRejectedRows] = useState([]);
    const [isApproved, setIsApproved] = useState(false);
    const [imageLoadErrors, setImageLoadErrors] = useState(new Set());

    const fileInputRef = useRef(null);
    const [partnerData, setPartnerData] = useState({});
    const [rowId, setRowId] = useState(null);

    // State for reject modal/dropdown
    const [showRejectDropdown, setShowRejectDropdown] = useState(false);
    const [rejectionDescription, setRejectionDescription] = useState("");

    // Add loading states for approve and reject
    const [isApproving, setIsApproving] = useState(false);
    const [isRejecting, setIsRejecting] = useState(false);

    // Add refresh key for forcing updates
    const [refreshKey, setRefreshKey] = useState(0);

    // NEW: Filter states like Userdetails file
    const [searchType, setSearchType] = useState("partnerId"); // Default search type
    const [searchQuery, setSearchQuery] = useState("");

    // Search options for dropdown
    const searchOptions = [
        { value: "partnerId", label: "Partner ID" },
        { value: "fullName", label: "Full Name" },
        { value: "email", label: "Email" },
        { value: "phoneNumber", label: "Mobile Number" },
        { value: "city", label: "City" }
    ];

    const getCurrentPartnerId = () => {
        return (
            selectedPartner?.id ||
            selectedPartner?.partnerId ||
            partnerDetails?.id ||
            null
        );
    };

    const partnerId = getCurrentPartnerId();

    // NEW: Format date function
    const formatDate = (dateString) => {
        if (!dateString) return "N/A";

        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return "Invalid Date";

            return date.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        } catch (error) {
            return "Invalid Date";
        }
    };

    // NEW: Get placeholder text based on search type
    const getPlaceholderText = () => {
        switch (searchType) {
            case "partnerId": return "Search Partner ID";
            case "fullName": return "Search Full Name";
            case "email": return "Search Email";
            case "phoneNumber": return "Search Mobile Number";
            case "city": return "Search City";
            default: return "Search...";
        }
    };

    // NEW: Clear all filters
    const clearAllFilters = () => {
        setSearchQuery("");
        setVehicleFilter("");
        setCityFilter("");
    };

    // NEW: Search filtered data
    const searchFilteredData = useMemo(() => {
        if (!searchQuery.trim() && !vehicleFilter && !cityFilter) {
            return partnersData;
        }

        const query = searchQuery.toLowerCase().trim();

        return partnersData.filter(partner => {
            // Search filter
            let searchMatch = true;
            if (searchQuery.trim()) {
                let fieldValue;

                if (searchType === "email") {
                    fieldValue = partner.email || partner.maskedEmail;
                } else {
                    fieldValue = partner[searchType];
                }

                if (!fieldValue) searchMatch = false;

                // Special handling for mobile number search
                if (searchType === "phoneNumber") {
                    const cleanUserMobile = fieldValue.toString().replace(/\D/g, '');
                    const cleanQuery = query.replace(/\D/g, '');
                    searchMatch = fieldValue.toString().toLowerCase().includes(query) ||
                        cleanUserMobile.includes(cleanQuery);
                } else {
                    // For other fields
                    searchMatch = fieldValue.toString().toLowerCase().includes(query);
                }
            }

            // Vehicle filter
            let vehicleMatch = true;
            if (vehicleFilter) {
                vehicleMatch = partner.vehicleTypes?.includes(vehicleFilter) ||
                    partner.vehicles?.includes(vehicleFilter);
            }

            // City filter
            let cityMatch = true;
            if (cityFilter) {
                cityMatch = partner.city?.toLowerCase().includes(cityFilter.toLowerCase());
            }

            return searchMatch && vehicleMatch && cityMatch;
        });
    }, [partnersData, searchQuery, searchType, vehicleFilter, cityFilter]);

    // NEW: Export function
    const handleExportFiltered = () => {
        const exportData = searchFilteredData.length > 0 ? searchFilteredData : partnersData;

        if (exportData.length === 0) {
            alert("There is no data to export.");
            return;
        }

        const escapeCsvField = (field) => {
            const str = String(field ?? '');
            if (str.includes(',') || str.includes('"') || str.includes('\n')) {
                return `"${str.replace(/"/g, '""')}"`;
            }
            return str;
        };

        const headers = [
            "S.NO",
            "Partner ID",
            "Full Name",
            "Email",
            "Phone Number",
            "City",
            "Vehicle Types",
            "Verified Status",
            "Online Status"
        ];

        // Map the filtered data for export
        const csvRows = exportData.map((partner, index) => [
            index + 1,
            partner.partnerId || 'N/A',
            partner.fullName || 'N/A',
            partner.email || partner.maskedEmail || 'N/A',
            partner.phoneNumber || partner.maskedPhoneNumber || 'N/A',
            partner.city || 'N/A',
            Array.isArray(partner.vehicleTypes) ? partner.vehicleTypes.join(", ") : 'N/A',
            partner.verified ? "Verified" : "Pending",
            partner.online ? "Online" : "Offline"
        ]);

        const csvString = [
            headers.map(escapeCsvField).join(','),
            ...csvRows.map(row => row.map(escapeCsvField).join(','))
        ].join('\r\n');

        const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", "subscribed_logistics_partners.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleImageError = (imageUrl, fieldName, index) => {
        const errorKey = `${fieldName}-${index}`;
        setImageLoadErrors(prev => new Set([...prev, errorKey]));
    };

    const handleImageLoad = (imageUrl, fieldName, index) => {
        const errorKey = `${fieldName}-${index}`;
        setImageLoadErrors(prev => {
            const newSet = new Set(prev);
            newSet.delete(errorKey);
            return newSet;
        });
    };

    const isImageFailed = (fieldName, index) => {
        return imageLoadErrors.has(`${fieldName}-${index}`);
    };

    const fetchPartnerDetails = async (partnerId) => {
        if (!partnerId) return;

        setLoading(true);

        const token = localStorage.getItem("authToken");

        try {

            console.log(`🔍 FETCHING PARTNER DETAILS FOR ID: ${partnerId}`);

            // 🔥 SERVICE API
            const data = await getPartnerDetails(partnerId, token);

            console.log("✅ API RESPONSE RECEIVED:", data);

            setPartnerDetails(data);

        } catch (err) {

            console.error("❌ get-partner-details error:", err);

            // FALLBACK DATA
            const listPartner = partnersData.find(
                p => p.id === partnerId || p.partnerId === partnerId
            );

            if (listPartner) {

                console.log("⚠️ Falling back to list data:", listPartner);

                setPartnerDetails(listPartner);
            }

        } finally {

            setLoading(false);
        }
    };

    // Function to refresh all data
    const refreshAllData = async () => {
        const token = localStorage.getItem("authToken");

        if (!token) {
            console.error("No auth token found");
            return;
        }

        showLoader();

        try {

            // 🔥 SERVICE API
            const data = await getAllPartners(token);

            console.log("📊 Refreshed partners data:", data);

            setPartnersData(data);

            if (selectedPartner) {

                const currentPartnerId =
                    selectedPartner?.id ||
                    selectedPartner?.partnerId;

                if (currentPartnerId) {
                    await fetchPartnerDetails(currentPartnerId);
                }
            }

        } catch (err) {

            console.error("Refresh data error:", err);

        } finally {

            hideLoader();
        }
    };
    // Save row using the update API endpoint
    const handleSaveRow = async (rowIndex) => {

        const token =
            localStorage.getItem("authToken") ||
            localStorage.getItem("token");

        if (!token) {
            alert("No authentication token found");
            return;
        }

        try {

            const partnerId = getCurrentPartnerId();

            if (!partnerId) {
                console.error("Partner ID not found");
                return;
            }

            const row = rows[rowIndex];

            console.log("Editing row:", row);

            const formData = new FormData();

            const partnerData = {};

            const fieldMapping = {
                "Full Name": "fullName",
                "Phone Number": "phoneNumber",
                "Email": "email",
                "AADHAR Number": "aadharNumber",
                "PAN Number": "panNumber",
                "License Number": "licenseNumber",
                "Vehicle Types": "vehicleTypes",
                "Vehicles": "vehicles",
                "Petrolium Types": "petroliumTypes",
                "RC Number": "rcNumber",
                "Insurance Number": "insuranceNumber",
            };

            const backendFieldName =
                fieldMapping[row.key];

            if (
                backendFieldName &&
                row.value &&
                row.value !== "N/A"
            ) {

                if (Array.isArray(row.value)) {
                    partnerData[backendFieldName] = row.value;

                } else if (typeof row.value === "string") {
                    partnerData[backendFieldName] = row.value;
                }
            }

            // ADDRESS
            if (
                row.key === "Current Address" &&
                typeof row.value === "object"
            ) {

                Object.keys(row.value).forEach(key => {
                    if (row.value[key] !== "N/A") {
                        partnerData[key] = row.value[key];
                    }
                });
            }

            // PERMANENT ADDRESS
            if (
                row.key === "Permanent Address" &&
                typeof row.value === "object"
            ) {

                Object.keys(row.value).forEach(key => {
                    if (row.value[key] !== "N/A") {
                        partnerData[key] = row.value[key];
                    }
                });
            }

            // BANK DETAILS
            if (
                row.key === "Bank Details" &&
                typeof row.value === "object"
            ) {

                Object.keys(row.value).forEach(key => {
                    if (row.value[key] !== "N/A") {
                        partnerData[key] = row.value[key];
                    }
                });
            }

            // JSON DATA
            formData.append(
                "partnerData",
                new Blob(
                    [JSON.stringify(partnerData)],
                    { type: "application/json" }
                )
            );

            // IMAGES
            if (row.images && row.images.length > 0) {

                row.images.forEach((img, idx) => {

                    if (img.file) {

                        const imageFieldName =
                            getImageFieldName(row.key, idx);

                        formData.append(
                            imageFieldName,
                            img.file
                        );
                    }
                });
            }

            // 🔥 SERVICE API
            await updatePartnerDetails(
                partnerId,
                formData,
                token
            );

            alert("Data updated successfully!");

            setRows(prev =>
                prev.map((r, idx) =>
                    idx === rowIndex
                        ? { ...r, isEditing: false }
                        : r
                )
            );

            fetchPartnerDetails(partnerId);

        } catch (err) {

            console.error("Error updating partner:", err);

            alert(
                "Error updating data: " +
                (err.response?.data?.message || err.message)
            );
        }
    };

    const getImageFieldName = (rowKey, imageIndex) => {
        const fieldMap = {
            "AADHAR Number": imageIndex === 0 ? "aadharCardFront" : "aadharCardBack",
            "PAN Number": "panCard",
            "License Number": imageIndex === 0 ? "licenseFront" : "licenseBack",
            "Profile Picture": "profilePicture",
            "RC Number": imageIndex === 0 ? "rcBookFront" : "rcBookBack",
        };

        return fieldMap[rowKey] || `image${imageIndex + 1}`;
    };

    useEffect(() => {

        const token =
            localStorage.getItem("authToken");

        if (!token) {
            console.error("No auth token found");
            return;
        }

        const fetchPartners = async () => {

            try {

                showLoader();

                // 🔥 SERVICE API
                const data =
                    await getAllDeliveryPartners(token);

                console.log("📊 Initial partners data:", data);

                setPartnersData(data);

            } catch (err) {

                console.error("Partners fetch error:", err);

            } finally {

                hideLoader();
            }
        };

        fetchPartners();

    }, [refreshKey]);

    useEffect(() => {
        let data = partnersData;

        if (vehicleFilter) data = data.filter((i) =>
            i.vehicleTypes?.includes(vehicleFilter) ||
            i.vehicles?.includes(vehicleFilter)
        );
        if (cityFilter) data = data.filter((i) => i.city === cityFilter);
        setFilteredData(data);
    }, [statusFilter, vehicleFilter, cityFilter, partnersData]);

    useEffect(() => {
        if (!selectedPartner) {
            setRows(getDefaultDeliveryPartnerRows());
            setVerifiedRows([]);
            setRejectedRows([]);
            setIsApproved(false);
            setImageLoadErrors(new Set());
            return;
        }

        const dataSource = partnerDetails || selectedPartner;

        console.log("📊 Data source for detail view:", dataSource);

        if (dataSource.verified === true) {
            setIsApproved(true);
        }

        const imageMap = {
            "AADHAR Number": [
                dataSource.aadharCardFront,
                dataSource.aadharCardBack
            ].filter(url => url && url !== "N/A" && typeof url === 'string' && url.trim() !== ''),
            "PAN Number": [dataSource.panCard]
                .filter(url => url && url !== "N/A" && typeof url === 'string' && url.trim() !== ''),
            "License Number": [
                dataSource.licenseFront,
                dataSource.licenseBack
            ].filter(url => url && url !== "N/A" && typeof url === 'string' && url.trim() !== ''),
            "Profile Picture": [dataSource.profilePicture]
                .filter(url => url && url !== "N/A" && typeof url === 'string' && url.trim() !== ''),
            "RC Number": [
                dataSource.rcBookFront,
                dataSource.rcBookBack
            ].filter(url => url && url !== "N/A" && typeof url === 'string' && url.trim() !== ''),
        };

        const newRows = [
            {
                key: "Full Name",
                value: dataSource.fullName || "N/A"
            },
            {
                key: "Phone Number",
                value: dataSource.phoneNumber || "N/A"
            },
            {
                key: "Email",
                value: dataSource.email || "N/A"
            },
            {
                key: "Profile Picture",
                value: "Profile Picture"
            },
            {
                key: "AADHAR Number",
                value: dataSource.aadharNumber || "N/A"
            },
            {
                key: "PAN Number",
                value: dataSource.panNumber || "N/A"
            },
            {
                key: "License Number",
                value: dataSource.licenseNumber || "N/A"
            },
            {
                key: "Vehicle Types",
                value: Array.isArray(dataSource.vehicleTypes)
                    ? dataSource.vehicleTypes
                    : (dataSource.vehicleTypes ? [dataSource.vehicleTypes] : [])
            },
            {
                key: "Vehicles",
                value: Array.isArray(dataSource.vehicles)
                    ? dataSource.vehicles
                    : (dataSource.vehicles ? [dataSource.vehicles] : [])
            },
            {
                key: "Petrolium Types",
                value: Array.isArray(dataSource.petroliumTypes)
                    ? dataSource.petroliumTypes
                    : (dataSource.petroliumTypes ? [dataSource.petroliumTypes] : [])
            },
            {
                key: "RC Number",
                value: Array.isArray(dataSource.rcNumber) && dataSource.rcNumber.length > 0
                    ? dataSource.rcNumber[0]
                    : dataSource.rcNumber || "N/A"
            },
            {
                key: "Insurance Number",
                value: Array.isArray(dataSource.insuranceNumber) && dataSource.insuranceNumber.length > 0
                    ? dataSource.insuranceNumber[0]
                    : dataSource.insuranceNumber || "N/A"
            },
            {
                key: "Bank Details",
                value: {
                    bankName: dataSource.bankName || "N/A",
                    accountNumber: dataSource.accountNumber || "N/A",
                    accountHolderName: dataSource.accountHolderName || "N/A",
                    ifscCode: dataSource.ifscCode || "N/A",
                    branchName: dataSource.branchName || "N/A",
                    upiId: dataSource.upiId || "N/A",
                },
            },
            {
                key: "Current Address",
                value: {
                    address: dataSource.address || "N/A",
                    city: dataSource.city || "N/A",
                    state: dataSource.state || "N/A",
                    pinCode: dataSource.pinCode || "N/A",
                    country: dataSource.country || "N/A",
                    houseNo: dataSource.houseNo || "N/A",
                    street: dataSource.street || "N/A",
                },
            },
            {
                key: "Permanent Address",
                value: {
                    permanentAddress: dataSource.permanentAddress || "N/A",
                    permanentCity: dataSource.permanentCity || "N/A",
                    permanentState: dataSource.permanentState || "N/A",
                    permanentPinCode: dataSource.permanentPinCode || "N/A",
                    permanentCountry: dataSource.permanentCountry || "N/A",
                    permanentHouseNo: dataSource.permanentHouseNo || "N/A",
                    permanentStreet: dataSource.permanentStreet || "N/A",
                },
            },
        ];

        const updatedRows = newRows.map((row) => ({
            ...row,
            images: imageMap[row.key] || [],
        }));

        console.log("🚨 DEBUG - Final rows to display:", updatedRows);

        setRows(updatedRows.map((r) => ({ ...r, isEditing: false })));
        setVerifiedRows([]);
        setRejectedRows([]);
        setImageLoadErrors(new Set());
    }, [selectedPartner, partnerDetails]);

    const handleViewClick = (partner) => {
        console.log("👁️ VIEW CLICKED FOR PARTNER:", partner);
        setSelectedPartner(partner);
        const partnerId = partner?.id || partner?.partnerId;
        console.log("🔍 FETCHING PARTNER DETAILS FOR ID:", partnerId);
        fetchPartnerDetails(partnerId);
    };

    const handleVerifyy = async (i) => {

        const normalisedKey =
            rows[i].key.trim().replace(/\.$/, "");

        const statusField =
            STATUS_FIELD_MAP[normalisedKey];

        if (!statusField) {
            console.warn("No status mapping for:", rows[i].key);
            return;
        }

        const partnerId =
            getCurrentPartnerId();

        if (!partnerId)
            return alert("Partner ID is missing");

        try {

            const updateData = {
                [statusField]: true
            };

            const formData = new FormData();

            formData.append(
                "partnerData",

                new Blob(
                    [JSON.stringify(updateData)],
                    {
                        type: "application/json"
                    }
                )
            );

            // 🔥 SERVICE API
            await verifyPartnerField(
                partnerId,
                formData,
                localStorage.getItem("authToken")
            );

            await fetchPartnerDetails(partnerId);

            alert("Partner field verified successfully!");

        } catch (err) {

            console.error("❌ Verify error:", err);

            alert(`Failed to verify field: ${err.message}`);
        }
    };

    // Reject individual field using the new API
    const handleReject = async (i) => {

        const normalisedKey =
            rows[i].key.trim().replace(/\.$/, "");

        const statusField =
            STATUS_FIELD_MAP[normalisedKey];

        if (!statusField) return;

        const partnerId =
            getCurrentPartnerId();

        if (!partnerId)
            return alert("Partner ID is missing");

        try {

            const updateData = {
                [statusField]: false
            };

            const formData =
                new FormData();

            formData.append(
                "partnerData",

                new Blob(
                    [JSON.stringify(updateData)],
                    {
                        type: "application/json"
                    }
                )
            );

            // 🔥 SERVICE API
            await rejectPartnerField(
                partnerId,
                formData,
                localStorage.getItem("authToken")
            );

            alert("Partner field rejected successfully!");

            fetchPartnerDetails(partnerId);

        } catch (err) {

            console.error("❌ Reject error:", err);

            alert(`Failed to reject field: ${err.message}`);
        }
    };

    // Approve all using the /admin/approval/{id} API endpoint
    const handleApproveAll = async () => {

        const partnerId =
            getCurrentPartnerId();

        if (!partnerId)
            return alert("Partner ID is missing");

        setIsApproving(true);

        try {

            const token =
                localStorage.getItem("authToken");

            if (!token)
                return alert("Authorization token missing");

            console.log(`✅ Approving partner ${partnerId}...`);

            // 🔥 SERVICE API
            const data = await approvePartner(
                partnerId,
                { approved: true },
                token
            );

            console.log("✅ Approval API response:", data);

            if (data && data.verified === true) {

                alert("Partner approved successfully!");

                setPartnerDetails(prev => ({
                    ...prev,
                    ...data,
                    verified: true
                }));

                setSelectedPartner(prev => ({
                    ...prev,
                    ...data,
                    verified: true
                }));

                setPartnersData(prev =>
                    prev.map(partner =>
                        partner.id === partnerId ||
                            partner.partnerId === partnerId
                            ? {
                                ...partner,
                                ...data,
                                verified: true
                            }
                            : partner
                    )
                );

                setIsApproved(true);

                setTimeout(() => {
                    setRefreshKey(prev => prev + 1);
                }, 1000);

            } else {

                alert("⚠️ Backend API not responding properly.");
            }

        } catch (err) {

            console.error("❌ Approve partner error:", err);

            const errorMessage =
                err.response?.data?.message ||
                err.response?.data?.error ||
                err.message;

            alert(`Failed to approve partner: ${errorMessage}`);

        } finally {

            setIsApproving(false);
        }
    };

    // Reject all with reason using the /admin/approval/{id} API endpoint
    const handleRejectAll = async () => {

        if (!rejectionDescription.trim()) {
            alert("Please enter a rejection reason");
            return;
        }

        const partnerId =
            getCurrentPartnerId();

        if (!partnerId)
            return alert("Partner ID is missing");

        setIsRejecting(true);

        try {

            const token =
                localStorage.getItem("authToken");

            if (!token)
                return alert("Authorization token missing");

            const requestBody = {
                verified: false,
                rejectReason: rejectionDescription.trim()
            };

            // 🔥 SERVICE API
            const data =
                await rejectPartnerApproval(
                    partnerId,
                    requestBody,
                    token
                );

            console.log("✅ Rejection API response:", data);

            setShowRejectDropdown(false);

            setRejectionDescription("");

            if (data) {

                alert("Partner verification rejected successfully!");

                setPartnersData(prev =>
                    prev.map(partner =>
                        partner.id === partnerId ||
                            partner.partnerId === partnerId
                            ? {
                                ...partner,
                                verified: false,
                                rejectReason: rejectionDescription
                            }
                            : partner
                    )
                );

                setPartnerDetails(prev => ({
                    ...prev,
                    verified: false,
                    rejectReason: rejectionDescription
                }));

                setSelectedPartner(prev => ({
                    ...prev,
                    verified: false,
                    rejectReason: rejectionDescription
                }));

                setIsApproved(false);

                setTimeout(() => {
                    setRefreshKey(prev => prev + 1);
                }, 1000);

            } else {

                throw new Error("Invalid response from server");
            }

        } catch (err) {

            console.error("❌ Reject error:", err);

            const errorMessage =
                err.response?.data?.message ||
                err.response?.data?.error ||
                err.message ||
                "Failed to reject partner";

            alert(`Failed to reject: ${errorMessage}`);

        } finally {

            setIsRejecting(false);
        }
    };

    const handleBackClick = () => setSelectedPartner(null);

    const handleFileChange = (e, index) => {
        const fileName = e.target.files[0]?.name || "";
        const updated = [...rows];
        updated[index].value = fileName;
        setRows(updated);
    };

    const ImageDisplay = ({ images, fieldName }) => {
        if (!images || images.length === 0) {
            return (
                <div className="text-center">
                    <span style={{ fontSize: "18px", color: "#6c757d" }}>-</span>
                </div>
            );
        }

        return (
            <div className="d-flex justify-content-center gap-2 flex-wrap">
                {images.map((img, idx) => {
                    const imageUrl = typeof img === 'string' ? img : img.url;
                    const hasFailed = isImageFailed(fieldName, idx);

                    if (hasFailed || !imageUrl) {
                        return (
                            <div key={idx} className="text-center">
                                <span style={{ fontSize: "18px", color: "#6c757d" }}>-</span>
                            </div>
                        );
                    }

                    return (
                        <div key={idx} className="text-center position-relative">
                            <img
                                src={imageUrl}
                                alt={`${fieldName}-${idx}`}
                                style={{
                                    width: fieldName === "Profile Picture" ? "80px" : "60px",
                                    height: fieldName === "Profile Picture" ? "80px" : "60px",
                                    borderRadius: "6px",
                                    objectFit: "cover",
                                    border: "1px solid #dee2e6",
                                }}
                                onError={() => handleImageError(imageUrl, fieldName, idx)}
                                onLoad={() => handleImageLoad(imageUrl, fieldName, idx)}
                            />
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-lg-12" style={{ marginTop: "10px" }}>
                    <div className="client" style={{ overflowY: "auto", height: "500px" }}>
                        {!selectedPartner ? (
                            <>
                                {/* NEW: Filter Controls like Userdetails file */}
                                <div className="row mb-3 g-2 align-items-center">
                                    {/* Search Type Dropdown and Search Input */}
                                    <div className="col-md-auto col-12">
                                        <div className="d-flex align-items-center" style={{ gap: '10px' }}>
                                            {/* Search Type Dropdown */}
                                            <div style={{ minWidth: '150px' }}>
                                                <select
                                                    className="form-select"
                                                    value={searchType}
                                                    onChange={(e) => setSearchType(e.target.value)}
                                                    style={{ height: "40px" }}
                                                >
                                                    {searchOptions.map(option => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            {/* Search Input */}
                                            <div style={{ minWidth: '200px' }}>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder={getPlaceholderText()}
                                                    value={searchQuery}
                                                    onChange={(e) => setSearchQuery(e.target.value)}
                                                    style={{ height: "40px" }}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Vehicle Filter */}
                                    <div className="col-md-auto col-12">
                                        <select
                                            className="form-select"
                                            value={vehicleFilter}
                                            onChange={(e) => setVehicleFilter(e.target.value)}
                                            style={{ maxWidth: '150px', height: "40px" }}
                                        >
                                            <option value="">All Vehicles</option>
                                            {vehicleTypeOptions.map(option => (
                                                <option key={option} value={option}>{option}</option>
                                            ))}
                                        </select>
                                    </div>



                                    {/* Clear Filters Button */}
                                    <div className="col-md-auto col-12">
                                        <button
                                            className="btn"
                                            onClick={clearAllFilters}
                                            style={{
                                                backgroundColor: '#6c757d',
                                                color: '#fff',
                                                height: "40px",
                                                padding: "0 15px"
                                            }}
                                        >
                                            Clear Filters
                                        </button>
                                    </div>

                                    {/* Export Button */}
                                    <div className="col-md col-12">
                                        <div className="d-flex justify-content-start justify-content-md-end">
                                            <button
                                                className="btn"
                                                onClick={handleExportFiltered}
                                                style={{
                                                    backgroundColor: 'rgb(230 109 51)',
                                                    color: '#fff',
                                                    height: "40px",
                                                    padding: "0 20px"
                                                }}
                                            >
                                                Export
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="sed pe-3">
                                    <table style={tableStyle} className="table table-striped">
                                        <thead>
                                            <tr>
                                                <th style={thStyle}>S.NO</th>
                                                <th style={thStyle}>Partner ID</th>
                                                <th style={thStyle}>Full Name</th>
                                                <th style={thStyle}>Email</th>
                                                <th style={thStyle}>Mobile</th>
                                                <th style={thStyle}>City</th>
                                                <th style={thStyle}>Vehicle Types</th>
                                                <th style={thStyle}>Status</th>
                                                <th style={thStyle}>Verified</th>
                                                {/* <th style={thStyle}>Actions</th> */}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {searchFilteredData.length ? (
                                                searchFilteredData.map((partner, idx) => (
                                                    <tr key={partner.id || idx}>
                                                        <td style={thTdStyle}>{idx + 1}</td>
                                                        <td style={thTdStyle}>{partner?.partnerId || "—"}</td>
                                                        <td style={thTdStyle}>{partner?.fullName || "—"}</td>
                                                        <td style={thTdStyle}>{partner?.maskedEmail || partner?.email || "—"}</td>
                                                        <td style={thTdStyle}>{partner?.maskedPhoneNumber || partner?.phoneNumber || "—"}</td>
                                                        <td style={thTdStyle}>{partner?.city || "—"}</td>
                                                        <td style={thTdStyle}>
                                                            {Array.isArray(partner?.vehicleTypes)
                                                                ? partner.vehicleTypes.join(", ")
                                                                : "—"}
                                                        </td>
                                                        <td style={thTdStyle}>
                                                            <span className={`badge ${partner.online ? 'bg-primary' : 'bg-secondary'}`}>
                                                                {partner.online ? 'Online' : 'Offline'}
                                                            </span>
                                                        </td>
                                                        <td style={thTdStyle}>
                                                            <span className="badge bg-success">
                                                                Verified
                                                            </span>
                                                        </td>
                                                        {/* <td style={thTdStyle}>
                                                            <button
                                                                style={{ ...buttonStyle, color: "rgb(230 109 51)", fontWeight: "bold" }}
                                                                onClick={() => handleViewClick(partner)}
                                                            >
                                                                View
                                                            </button>
                                                        </td> */}
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td style={thTdStyle} colSpan={10}>
                                                        {partnersData.length === 0
                                                            ? "Loading data or no subscribed partners found."
                                                            : (searchQuery.trim() || vehicleFilter || cityFilter)
                                                                ? "No subscribed partners found with the specified filters."
                                                                : "No subscribed partners available."}
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        ) : (
                            <div className="mt-4 rounded-3 shadow-sm">
                                <div className="d-flex align-items-center mb-4">
                                    <button
                                        onClick={handleBackClick}
                                        className="btn fw-bold px-4 py-2"
                                        style={{ backgroundColor: "rgb(230 109 51)", color: "#fff" }}
                                    >
                                        Back to List
                                    </button>
                                    <button
                                        onClick={refreshAllData}
                                        className="btn btn-outline-primary ms-2"
                                    >
                                        Refresh Data
                                    </button>
                                </div>

                                {loading && (
                                    <div className="text-center my-4">
                                        <span className="spinner-border text-primary" />
                                    </div>
                                )}

                                {!loading && (
                                    <>
                                        <div className="register mt-4">
                                            <div className="registertable bo2">
                                                <table className="table table-bordered table-striped align-middle mt-4 w-100">
                                                    <thead>
                                                        <tr>
                                                            <th style={detailThStyle}>Field</th>
                                                            <th style={detailThStyle}>Value</th>
                                                            <th style={detailThStyle}>Image</th>
                                                            <th style={detailThStyle}>Status</th>
                                                        </tr>
                                                    </thead>

                                                    <tbody>
                                                        {rows.map((r, i) => {
                                                            const isEditing = r.isEditing || false;

                                                            const handleInputChange = (field, value) => {
                                                                setRows((prevRows) =>
                                                                    prevRows.map((row, idx) =>
                                                                        idx === i
                                                                            ? {
                                                                                ...row,
                                                                                value:
                                                                                    typeof row.value === "object"
                                                                                        ? { ...row.value, [field]: value }
                                                                                        : value,
                                                                            }
                                                                            : row
                                                                    )
                                                                );
                                                            };

                                                            const handleArraySelection = (selectedValue, arrayField) => {
                                                                setRows((prevRows) =>
                                                                    prevRows.map((row, idx) => {
                                                                        if (idx === i) {
                                                                            const currentValues = Array.isArray(row.value) ? row.value : [];
                                                                            const isSelected = currentValues.includes(selectedValue);

                                                                            if (isSelected) {
                                                                                return {
                                                                                    ...row,
                                                                                    value: currentValues.filter(v => v !== selectedValue)
                                                                                };
                                                                            } else {
                                                                                return {
                                                                                    ...row,
                                                                                    value: [...currentValues, selectedValue]
                                                                                };
                                                                            }
                                                                        }
                                                                        return row;
                                                                    })
                                                                );
                                                            };

                                                            const handleImageChange = (e) => {
                                                                const files = Array.from(e.target.files);
                                                                const newImages = files.map((file) => ({
                                                                    url: URL.createObjectURL(file),
                                                                    file
                                                                }));

                                                                setRows(prev =>
                                                                    prev.map((row, idx) =>
                                                                        idx === i
                                                                            ? {
                                                                                ...row,
                                                                                images: [...(row.images || []), ...newImages]
                                                                            }
                                                                            : row
                                                                    )
                                                                );
                                                            };

                                                            const getOptions = () => {
                                                                switch (r.key) {
                                                                    case "Vehicle Types": return vehicleTypeOptions;
                                                                    case "Vehicles": return vehiclesOptions;
                                                                    case "Petrolium Types": return petroliumTypeOptions;
                                                                    default: return [];
                                                                }
                                                            };

                                                            return (
                                                                <tr key={r.key || i}>
                                                                    <td className="fw-bold text-center align-middle">{r.key}</td>

                                                                    <td className="text-start align-middle">
                                                                        {r.key === "Vehicle Types" || r.key === "Vehicles" || r.key === "Petrolium Types" ? (
                                                                            isEditing ? (
                                                                                <div className="d-flex flex-column gap-2">
                                                                                    {getOptions().map((option) => {
                                                                                        const currentValues = Array.isArray(r.value) ? r.value : [];
                                                                                        const isSelected = currentValues.includes(option);

                                                                                        return (
                                                                                            <div key={option} className="form-check">
                                                                                                <input
                                                                                                    className="form-check-input"
                                                                                                    type="checkbox"
                                                                                                    id={`${r.key}-${option}-${i}`}
                                                                                                    checked={isSelected}
                                                                                                    onChange={() => handleArraySelection(option, r.key)}
                                                                                                />
                                                                                                <label
                                                                                                    className="form-check-label"
                                                                                                    htmlFor={`${r.key}-${option}-${i}`}
                                                                                                >
                                                                                                    {option}
                                                                                                </label>
                                                                                            </div>
                                                                                        );
                                                                                    })}
                                                                                </div>
                                                                            ) : (
                                                                                Array.isArray(r.value) && r.value.length > 0
                                                                                    ? r.value.join(", ")
                                                                                    : "N/A"
                                                                            )
                                                                        ) : r.key === "Bank Details" ? (
                                                                            Object.entries(r.value || {}).map(([field, value]) => (
                                                                                <div key={field} className="mb-1">
                                                                                    <strong>{field.replace(/([A-Z])/g, " $1")}:</strong>{" "}
                                                                                    {isEditing ? (
                                                                                        <input
                                                                                            type="text"
                                                                                            value={value || ""}
                                                                                            onChange={(e) => handleInputChange(field, e.target.value)}
                                                                                            className="form-control d-inline-block w-75 ms-2"
                                                                                        />
                                                                                    ) : (
                                                                                        safeDisplay(value)
                                                                                    )}
                                                                                </div>
                                                                            ))
                                                                        ) : r.key === "Current Address" || r.key === "Permanent Address" ? (
                                                                            Object.entries(r.value || {}).map(([field, value]) => (
                                                                                <div key={field} className="mb-1">
                                                                                    <strong>{field.charAt(0).toUpperCase() + field.slice(1)}:</strong>{" "}
                                                                                    {isEditing ? (
                                                                                        <input
                                                                                            type="text"
                                                                                            value={value || ""}
                                                                                            onChange={(e) => handleInputChange(field, e.target.value)}
                                                                                            className="form-control d-inline-block w-75 ms-2"
                                                                                        />
                                                                                    ) : (
                                                                                        safeDisplay(value)
                                                                                    )}
                                                                                </div>
                                                                            ))
                                                                        ) : r.key === "Profile Picture" ? (
                                                                            "Profile Picture"
                                                                        ) : r.key === "RC Number" || r.key === "Insurance Number" ? (
                                                                            isEditing ? (
                                                                                <input
                                                                                    type="text"
                                                                                    value={r.value || ""}
                                                                                    onChange={(e) => handleInputChange("value", e.target.value)}
                                                                                    className="form-control d-inline-block w-75"
                                                                                    placeholder="Enter comma-separated values"
                                                                                />
                                                                            ) : (
                                                                                safeDisplay(r.value)
                                                                            )
                                                                        ) : (
                                                                            isEditing ? (
                                                                                <input
                                                                                    type="text"
                                                                                    value={r.value || ""}
                                                                                    onChange={(e) => handleInputChange("value", e.target.value)}
                                                                                    className="form-control d-inline-block w-75"
                                                                                />
                                                                            ) : (
                                                                                safeDisplay(r.value)
                                                                            )
                                                                        )}
                                                                    </td>

                                                                    <td className="text-center align-middle">
                                                                        {isEditing ? (
                                                                            <>
                                                                                <input
                                                                                    type="file"
                                                                                    multiple={r.key === "AADHAR Number" || r.key === "License Number" || r.key === "RC Number"}
                                                                                    accept="image/*"
                                                                                    onChange={handleImageChange}
                                                                                    className="form-control mb-2"
                                                                                />
                                                                                <ImageDisplay images={r.images} fieldName={r.key} />
                                                                            </>
                                                                        ) : (
                                                                            <ImageDisplay images={r.images} fieldName={r.key} />
                                                                        )}
                                                                    </td>

                                                                    <td className="text-center align-middle">
                                                                        {isEditing ? (
                                                                            <>
                                                                                <button
                                                                                    className="btn btn-sm btn-success me-2"
                                                                                    style={{ minWidth: 80 }}
                                                                                    onClick={() => handleSaveRow(i)}
                                                                                >
                                                                                    Save
                                                                                </button>
                                                                                <button
                                                                                    className="btn btn-sm btn-secondary"
                                                                                    style={{ minWidth: 80 }}
                                                                                    onClick={() =>
                                                                                        setRows((prev) =>
                                                                                            prev.map((row, idx) =>
                                                                                                idx === i ? { ...row, isEditing: false } : row
                                                                                            )
                                                                                        )
                                                                                    }
                                                                                >
                                                                                    Cancel
                                                                                </button>
                                                                            </>
                                                                        ) : (
                                                                            <div style={{ display: "flex", gap: "10px" }}>
                                                                                <button
                                                                                    className={`btn btn-sm ${isFieldVerified(r.key, r.value, partnerDetails)
                                                                                        ? "btn-success"
                                                                                        : "btn-warning"
                                                                                        }`}
                                                                                    style={{ minWidth: 80 }}
                                                                                    onClick={() => handleVerifyy(i)}
                                                                                    disabled={
                                                                                        isFieldVerified(r.key, r.value, partnerDetails) ||
                                                                                        isFieldRejected(r.key, partnerDetails)
                                                                                    }
                                                                                >
                                                                                    {isFieldVerified(r.key, r.value, partnerDetails) ? "Verified" : "Verify"}
                                                                                </button>

                                                                                <button
                                                                                    className={`btn btn-sm ${isFieldRejected(r.key, partnerDetails) ? "btn-danger" : "btn-secondary"
                                                                                        }`}
                                                                                    style={{ minWidth: 80 }}
                                                                                    onClick={() => handleReject(i)}
                                                                                    disabled={
                                                                                        isFieldRejected(r.key, partnerDetails) ||
                                                                                        isFieldVerified(r.key, r.value, partnerDetails)
                                                                                    }
                                                                                >
                                                                                    {isFieldRejected(r.key, partnerDetails) ? "Rejected" : "Reject"}
                                                                                </button>

                                                                                <button
                                                                                    className="btn btn-sm btn-primary"
                                                                                    style={{ minWidth: 80 }}
                                                                                    onClick={() =>
                                                                                        setRows((prev) =>
                                                                                            prev.map((row, idx) =>
                                                                                                idx === i ? { ...row, isEditing: true } : row
                                                                                            )
                                                                                        )
                                                                                    }
                                                                                >
                                                                                    Edit
                                                                                </button>
                                                                            </div>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>

                                        <div className="d-flex justify-content-center gap-3 mt-4 mb-4 p-3 border-top position-relative">
                                            <div className="d-flex gap-3 align-items-start position-relative">
                                                {partnerDetails?.verified === true || isApproved ? (
                                                    <div className="d-flex align-items-center gap-3">
                                                        <span className="badge bg-success p-2 fs-6">Approved</span>
                                                    </div>
                                                ) : (
                                                    <button
                                                        className="btn btn-success px-4 py-2 fw-bold"
                                                        style={{ minWidth: "120px" }}
                                                        onClick={handleApproveAll}
                                                        disabled={isApproving || isRejecting}
                                                    >
                                                        {isApproving ? (
                                                            <>
                                                                <span className="spinner-border spinner-border-sm me-2"></span>
                                                                Approving...
                                                            </>
                                                        ) : (
                                                            "Approve"
                                                        )}
                                                    </button>
                                                )}
                                                {!(partnerDetails?.verified === true || isApproved) && (
                                                    <div className="position-relative">
                                                        {showRejectDropdown ? (
                                                            <div className="card p-3 shadow-sm" style={{
                                                                width: "300px",
                                                                position: "absolute",
                                                                top: "100%",
                                                                left: "0",
                                                                marginTop: "0px",
                                                                zIndex: 1000
                                                            }}>
                                                                <div className="mb-3">
                                                                    <label className="form-label fw-bold">Rejection Reason</label>
                                                                    <textarea
                                                                        className="form-control"
                                                                        rows="3"
                                                                        placeholder="Enter rejection reason..."
                                                                        value={rejectionDescription}
                                                                        onChange={(e) => setRejectionDescription(e.target.value)}
                                                                        disabled={isApproving || isRejecting}
                                                                    />
                                                                </div>

                                                                <div className="d-flex gap-2">
                                                                    <button
                                                                        className="btn btn-danger flex-grow-1"
                                                                        onClick={handleRejectAll}
                                                                        disabled={!rejectionDescription.trim() || isApproving || isRejecting}
                                                                    >
                                                                        {isRejecting ? (
                                                                            <>
                                                                                <span className="spinner-border spinner-border-sm me-2"></span>
                                                                                Rejecting...
                                                                            </>
                                                                        ) : (
                                                                            "Confirm Reject"
                                                                        )}
                                                                    </button>
                                                                    <button
                                                                        className="btn btn-secondary"
                                                                        onClick={() => {
                                                                            setShowRejectDropdown(false);
                                                                            setRejectionDescription("");
                                                                        }}
                                                                        disabled={isApproving || isRejecting}
                                                                    >
                                                                        Cancel
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <button
                                                                className="btn btn-danger px-4 py-2 fw-bold"
                                                                style={{ minWidth: "120px" }}
                                                                onClick={() => setShowRejectDropdown(true)}
                                                                disabled={isApproving || isRejecting}
                                                            >
                                                                {isRejecting ? (
                                                                    <>
                                                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                                                        Rejecting...
                                                                    </>
                                                                ) : (
                                                                    "Reject"
                                                                )}
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const tableStyle = { width: "100%", borderCollapse: "collapse", marginTop: 5 };
const thTdStyle = { border: "1px solid #ddd", textAlign: "center" };
const thStyle = { ...thTdStyle, backgroundColor: "rgb(230 109 51)", color: "#fff", fontWeight: "bold", maxWidth: "70px" };
const buttonStyle = { backgroundColor: "transparent", border: "none", cursor: "pointer" };

const detailThStyle = {
    ...thTdStyle,
    backgroundColor: "rgb(230 109 51)",
    color: "#fff",
    fontWeight: 600,
    padding: "0.75rem",
    verticalAlign: "middle",
    border: "1px solid #dee2e6",
    textAlign: "center"
};

export default Logisticssubscribedclients;